import React from "react";
import ReactDOM from "react-dom/client";
import { SXAlert } from "../components/SXAlert";

export type ErrorType = "error_" | "warning_" | "warning_unstable" | "information_";

// The React root behind each alert container, so an alert can be torn down properly wherever it
// is dismissed from. Weak so a container that is removed some other way does not pin its root.
const alertRoots = new WeakMap<HTMLElement, ReactDOM.Root>();

const ALERT_STACK_ID = "sx-alert-stack";

// One shared, bottom-anchored column that every alert is appended to.
//
// Alerts used to be positioned individually, each given a "bottom" percentage worked out from how
// many were on screen when it was created. That collided two ways. The count was taken modulo 4,
// so a fifth alert was placed exactly on top of the first; and the figure went stale as soon as
// an alert in the middle was dismissed, because the freed slot was not accounted for and the next
// alert landed on top of one that was still showing. Letting the browser lay them out in a column
// removes the arithmetic altogether, and copes with alerts whose text wraps onto several lines -
// a fixed 5% step assumed every alert was the same height.
const getAlertStack = (): HTMLElement => {
  const existing = document.getElementById(ALERT_STACK_ID);
  if (existing) {
    return existing;
  }

  const stack = document.createElement("div");
  stack.id = ALERT_STACK_ID;
  stack.style.position = "fixed";
  stack.style.right = "6%";
  stack.style.bottom = "5%";
  stack.style.display = "flex";
  stack.style.flexDirection = "column-reverse"; // newest sits closest to the bottom, older ones rise
  stack.style.alignItems = "flex-end";
  stack.style.gap = "10px";
  stack.style.zIndex = "9999";
  stack.style.pointerEvents = "none"; // the column spans empty space; only the alerts take clicks
  document.body.appendChild(stack);
  return stack;
};

// Take an alert off the screen: detach the container synchronously, so the duplicate check below
// sees an accurate picture immediately, and unmount its root in a microtask, because alerts
// dismiss themselves from inside an effect and React warns when a root is unmounted synchronously
// during a render pass.
const disposeAlert = (node: HTMLElement) => {
  const root = alertRoots.get(node);
  alertRoots.delete(node);
  node.remove();
  if (root) {
    queueMicrotask(() => root.unmount());
  }
};

// Remove every alert currently in the DOM. Call this when starting a fresh analysis so messages
// from a previous run are not left hanging around.
export const clearAlerts = () => {
  document.querySelectorAll<HTMLElement>("[id^='errormsg-']").forEach(disposeAlert);
};

export const AlertMessage = (
  type: ErrorType,
  status: string | React.JSX.Element,
  context: string,
  text: string | React.JSX.Element,
  keep = false,
) => {
  const trimmedText = text.toString().trim();

  // An identical message already on screen is replaced, not swallowed. Swallowing it meant that
  // repeating the same invalid action - picking an unstable support combination, say - produced
  // no feedback at all the second time, even though the action was still being rejected.
  // Replacing still collapses the double-fired-effect case this guard was originally added for
  // (create, dispose, create leaves exactly one alert), while letting every genuine attempt
  // produce a visible alert. We compare against a synchronously-set data attribute rather than
  // the React-rendered ".ng-alert-text" content, because that content is painted asynchronously,
  // so a second call in the same tick would read an empty textContent and stack a duplicate.
  for (const node of document.querySelectorAll<HTMLElement>("[id^='errormsg-']")) {
    const alertText = node.dataset.alertText ?? node.querySelector(".ng-alert-text")?.textContent;
    if (alertText && alertText.trim() === trimmedText) {
      disposeAlert(node);
    }
  }

  const domNode: HTMLElement = document.createElement("div");
  domNode.id = `errormsg-${Date.now()}`;
  domNode.dataset.alertText = trimmedText; // set synchronously so duplicate checks work before React renders
  domNode.style.pointerEvents = "auto"; // the stack around it does not take clicks

  getAlertStack().appendChild(domNode);

  try {
    const root = ReactDOM.createRoot(domNode);
    alertRoots.set(domNode, root);
    // AlertMessage owns the container and the root, so it is what cleans them up. Leaving the
    // container behind used to leave its data-alert-text marker in the DOM after the alert had
    // faded, which made the check above suppress that message for the rest of the session.
    const onDismiss = () => disposeAlert(domNode);
    const common = { status, context, text, onDismiss, inFlow: true };
    switch (type) {
      case "error_":
        root.render(<SXAlert {...common} variant="danger" keep={keep} />);
        break;
      case "warning_":
        root.render(<SXAlert {...common} variant="warning" keep={keep} />);
        break;
      case "warning_unstable":
        root.render(<SXAlert {...common} variant="warning" keep={true} />);
        break;
      case "information_":
        root.render(<SXAlert {...common} variant="info" />);
        break;
      default:
        break;
    }
  } catch (e) {
    console.log(e);
  }
};
